'use client';

import { c as _c } from "react/compiler-runtime";
import { type GetThesesRow, useGetThesesInfinite, useGetUserActivityInfinite, usePrefetchGetTheses, usePrefetchGetUserActivity } from '@morfi/backend-sdk-v2/client';
import { useTranslations } from 'next-intl';
import { useEffect, useId, useState } from 'react';
import { PortfolioListTransition } from '@/components/portfolio/positions-card/portfolio-list-transition';
import { PortfolioPanelHeader } from '@/components/portfolio/shared/portfolio-panel-header';
import { Edit03IconSolid } from '@/components/ui/icon/glyphs';
import { Panel, PanelContent } from '@/components/ui/panel';
import { SegmentedControl } from '@/components/ui/segmented-control';
import { useLiveListData } from '@/hooks/sdk/use-live-list-data';
import { useViewportActivation } from '@/hooks/ui/use-viewport-activation';
import { useFeatureGate } from '@/hooks/use-feature-gate';
import { ACTIVITY_FEED_FIELDS } from '@/lib/sdk/projections';
import { logger } from '@/lib/telemetry/logger';
import { cn } from '@/lib/ui/utils';
import { isActivityAttributedToWallet, isThesisAttributedToWallet } from '@/lib/wallet/multi-wallet-scope';
import { PortfolioActivityCount } from './portfolio-activity-count';
import { PortfolioActivityFeed } from './portfolio-activity-feed';
import { ACTIVITY_ICONS, ACTIVITY_KINDS, type ActivityFilter, matchesActivityFilter } from './portfolio-activity-filter';
import { PortfolioThesesContent } from './portfolio-theses-content';
export function PortfolioActivityPanel(t0) {
  const $ = _c(115);
  const {
    userId,
    walletPubkeys,
    giftUserIds,
    isOwn,
    attributionWalletAddress,
    className
  } = t0;
  const t = useTranslations("profile.stats");
  const {
    ref: panelRef,
    enabled,
    activate
  } = useViewportActivation();
  const id = useId();
  const [view, setView] = useState("theses");
  const prefetchTheses = usePrefetchGetTheses();
  const prefetchActivity = usePrefetchGetUserActivity();
  const giftingGate = useFeatureGate("gifting");
  const giftsEnabled = isOwn && giftingGate === true;
  const activeView = view === "gifts" && !giftsEnabled ? "theses" : view;
  let t1;
  if ($[0] !== userId || $[1] !== walletPubkeys) {
    t1 = {
      authorId: userId,
      walletPubkeys,
      sort: "recent"
    };
    $[0] = userId;
    $[1] = walletPubkeys;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const t2 = enabled && activeView === "theses";
  let t3;
  if ($[3] !== t2) {
    t3 = {
      enabled: t2
    };
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const theses = useGetThesesInfinite(t1, t3);
  let t4;
  if ($[5] !== giftUserIds || $[6] !== giftsEnabled) {
    t4 = giftsEnabled ? giftUserIds : [];
    $[5] = giftUserIds;
    $[6] = giftsEnabled;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const t5 = activeView === "theses" ? undefined : ACTIVITY_KINDS[activeView];
  let t6;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = [...ACTIVITY_FEED_FIELDS];
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== t4 || $[10] !== t5 || $[11] !== userId || $[12] !== walletPubkeys) {
    t7 = {
      userId,
      walletPubkeys,
      giftUserIds: t4,
      kind: t5,
      fields: t6
    };
    $[9] = t4;
    $[10] = t5;
    $[11] = userId;
    $[12] = walletPubkeys;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const t8 = enabled && activeView !== "theses";
  let t9;
  if ($[14] !== t8) {
    t9 = {
      enabled: t8
    };
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  const activity = useGetUserActivityInfinite(t7, t9);
  let t10;
  let t11;
  if ($[16] !== theses.error) {
    t10 = () => {
      if (theses.error) {
        logger.warn("portfolio.theses.load_failed", {
          errorName: theses.error.name
        });
      }
    };
    t11 = [theses.error];
    $[16] = theses.error;
    $[17] = t10;
    $[18] = t11;
  } else {
    t10 = $[17];
    t11 = $[18];
  }
  useEffect(t10, t11);
  const thesisRows = useLiveListData(theses.queryKey, theses.rows);
  let t12;
  if ($[19] !== attributionWalletAddress || $[20] !== thesisRows) {
    let t13;
    if ($[22] !== attributionWalletAddress) {
      t13 = row => attributionWalletAddress === null || isThesisAttributedToWallet(row, attributionWalletAddress);
      $[22] = attributionWalletAddress;
      $[23] = t13;
    } else {
      t13 = $[23];
    }
    t12 = thesisRows.filter(t13);
    $[19] = attributionWalletAddress;
    $[20] = thesisRows;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  const visibleTheses = t12;
  const selectedQuery = activeView === "theses" ? theses : activity;
  let t13;
  if ($[24] !== activeView || $[25] !== activity || $[26] !== attributionWalletAddress || $[27] !== visibleTheses) {
    t13 = activeView === "theses" ? visibleTheses.length : activity.rows.filter(row_0 => matchesActivityFilter(row_0, activeView) && (attributionWalletAddress === null || isActivityAttributedToWallet(row_0, attributionWalletAddress))).length;
    $[24] = activeView;
    $[25] = activity;
    $[26] = attributionWalletAddress;
    $[27] = visibleTheses;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  const count = t13;
  let t14;
  if ($[29] !== activeView || $[30] !== giftUserIds || $[31] !== giftsEnabled || $[32] !== prefetchActivity || $[33] !== prefetchTheses || $[34] !== userId || $[35] !== walletPubkeys) {
    t14 = function prefetchSegment(next) {
      if (next === activeView) {
        return;
      }
      if (next === "theses") {
        prefetchTheses({
          authorId: userId,
          walletPubkeys,
          sort: "recent"
        });
      } else {
        if (Object.hasOwn(ACTIVITY_KINDS, next) && (next !== "gifts" || giftsEnabled)) {
          prefetchActivity({
            userId,
            walletPubkeys,
            giftUserIds: giftsEnabled ? giftUserIds : [],
            kind: ACTIVITY_KINDS[next as ActivityFilter],
            fields: [...ACTIVITY_FEED_FIELDS]
          });
        }
      }
    };
    $[29] = activeView;
    $[30] = giftUserIds;
    $[31] = giftsEnabled;
    $[32] = prefetchActivity;
    $[33] = prefetchTheses;
    $[34] = userId;
    $[35] = walletPubkeys;
    $[36] = t14;
  } else {
    t14 = $[36];
  }
  const prefetchSegment = t14;
  let t15;
  if ($[37] !== className) {
    t15 = cn("min-w-0", className);
    $[37] = className;
    $[38] = t15;
  } else {
    t15 = $[38];
  }
  let t16;
  if ($[39] !== t) {
    t16 = t("activity_title");
    $[39] = t;
    $[40] = t16;
  } else {
    t16 = $[40];
  }
  const t17 = !enabled || selectedQuery.isPending || selectedQuery.isError || selectedQuery.isPlaceholderData;
  let t18;
  if ($[41] !== count || $[42] !== selectedQuery.hasNextPage || $[43] !== t17) {
    t18 = <PortfolioActivityCount count={count} hasMore={selectedQuery.hasNextPage} pending={t17} />;
    $[41] = count;
    $[42] = selectedQuery.hasNextPage;
    $[43] = t17;
    $[44] = t18;
  } else {
    t18 = $[44];
  }
  let t19;
  if ($[45] !== t16 || $[46] !== t18) {
    t19 = <PortfolioPanelHeader title={<span className="inline-flex items-center gap-2">{t16}{t18}</span>} />;
    $[45] = t16;
    $[46] = t18;
    $[47] = t19;
  } else {
    t19 = $[47];
  }
  let t20;
  if ($[48] !== t) {
    t20 = t("theses_title");
    $[48] = t;
    $[49] = t20;
  } else {
    t20 = $[49];
  }
  let t21;
  if ($[50] !== t20) {
    t21 = {
      key: "theses",
      label: t20,
      icon: Edit03IconSolid
    };
    $[50] = t20;
    $[51] = t21;
  } else {
    t21 = $[51];
  }
  let t22;
  if ($[52] !== t) {
    t22 = t("activity_tabs.buys");
    $[52] = t;
    $[53] = t22;
  } else {
    t22 = $[53];
  }
  let t23;
  if ($[54] !== t22) {
    t23 = {
      key: "buys",
      label: t22,
      icon: ACTIVITY_ICONS.buys
    };
    $[54] = t22;
    $[55] = t23;
  } else {
    t23 = $[55];
  }
  let t24;
  if ($[56] !== t) {
    t24 = t("activity_tabs.sells");
    $[56] = t;
    $[57] = t24;
  } else {
    t24 = $[57];
  }
  let t25;
  if ($[58] !== t24) {
    t25 = {
      key: "sells",
      label: t24,
      icon: ACTIVITY_ICONS.sells
    };
    $[58] = t24;
    $[59] = t25;
  } else {
    t25 = $[59];
  }
  let t26;
  if ($[60] !== t) {
    t26 = t("activity_tabs.backings");
    $[60] = t;
    $[61] = t26;
  } else {
    t26 = $[61];
  }
  let t27;
  if ($[62] !== t26) {
    t27 = {
      key: "backings",
      label: t26,
      icon: ACTIVITY_ICONS.backings
    };
    $[62] = t26;
    $[63] = t27;
  } else {
    t27 = $[63];
  }
  let t28;
  if ($[64] !== t) {
    t28 = t("activity_tabs.claims");
    $[64] = t;
    $[65] = t28;
  } else {
    t28 = $[65];
  }
  let t29;
  if ($[66] !== t28) {
    t29 = {
      key: "claims",
      label: t28,
      icon: ACTIVITY_ICONS.claims
    };
    $[66] = t28;
    $[67] = t29;
  } else {
    t29 = $[67];
  }
  let t30;
  if ($[68] !== t) {
    t30 = t("activity_tabs.boosts");
    $[68] = t;
    $[69] = t30;
  } else {
    t30 = $[69];
  }
  let t31;
  if ($[70] !== t30) {
    t31 = {
      key: "boosts",
      label: t30,
      icon: ACTIVITY_ICONS.boosts
    };
    $[70] = t30;
    $[71] = t31;
  } else {
    t31 = $[71];
  }
  let t32;
  if ($[72] !== t) {
    t32 = t("activity_tabs.gifts");
    $[72] = t;
    $[73] = t32;
  } else {
    t32 = $[73];
  }
  const t33 = !giftsEnabled;
  let t34;
  if ($[74] !== t32 || $[75] !== t33) {
    t34 = {
      key: "gifts",
      label: t32,
      icon: ACTIVITY_ICONS.gifts,
      hidden: t33
    };
    $[74] = t32;
    $[75] = t33;
    $[76] = t34;
  } else {
    t34 = $[76];
  }
  let t35;
  if ($[77] !== t21 || $[78] !== t23 || $[79] !== t25 || $[80] !== t27 || $[81] !== t29 || $[82] !== t31 || $[83] !== t34) {
    t35 = [t21, t23, t25, t27, t29, t31, t34];
    $[77] = t21;
    $[78] = t23;
    $[79] = t25;
    $[80] = t27;
    $[81] = t29;
    $[82] = t31;
    $[83] = t34;
    $[84] = t35;
  } else {
    t35 = $[84];
  }
  let t36;
  if ($[85] !== activate || $[86] !== giftsEnabled) {
    t36 = next_0 => {
      activate();
      if (next_0 === "theses") {
        setView(next_0);
      } else {
        if (Object.hasOwn(ACTIVITY_KINDS, next_0) && (next_0 !== "gifts" || giftsEnabled)) {
          setView(next_0 as ActivityFilter);
        }
      }
    };
    $[85] = activate;
    $[86] = giftsEnabled;
    $[87] = t36;
  } else {
    t36 = $[87];
  }
  const t37 = `${id}-activity`;
  let t38;
  if ($[88] !== t) {
    t38 = t("activity_title");
    $[88] = t;
    $[89] = t38;
  } else {
    t38 = $[89];
  }
  let t39;
  if ($[90] !== activeView || $[91] !== prefetchSegment || $[92] !== t35 || $[93] !== t36 || $[94] !== t37 || $[95] !== t38) {
    t39 = <div className="-mx-4 min-w-0 overflow-x-auto px-4 scrollbar-none"><SegmentedControl items={t35} active={activeView} onChange={t36} onItemPointerEnter={prefetchSegment} layoutId={t37} variant="tab" indicatorClassName="bg-background-tertiary dark:bg-background-tertiary" asTabs={true} ariaLabel={t38} /></div>;
    $[90] = activeView;
    $[91] = prefetchSegment;
    $[92] = t35;
    $[93] = t36;
    $[94] = t37;
    $[95] = t38;
    $[96] = t39;
  } else {
    t39 = $[96];
  }
  let t40;
  if ($[97] !== activeView || $[98] !== activity || $[99] !== attributionWalletAddress || $[100] !== isOwn || $[101] !== theses || $[102] !== visibleTheses) {
    t40 = activeView === "theses" ? <PortfolioThesesContent query={theses} rows={visibleTheses} isOwn={isOwn} /> : <PortfolioActivityFeed key={activeView} query={activity} isOwn={isOwn} attributionWalletAddress={attributionWalletAddress} filter={activeView} />;
    $[97] = activeView;
    $[98] = activity;
    $[99] = attributionWalletAddress;
    $[100] = isOwn;
    $[101] = theses;
    $[102] = visibleTheses;
    $[103] = t40;
  } else {
    t40 = $[103];
  }
  let t41;
  if ($[104] !== activeView || $[105] !== t40) {
    t41 = <PortfolioListTransition view={activeView}>{t40}</PortfolioListTransition>;
    $[104] = activeView;
    $[105] = t40;
    $[106] = t41;
  } else {
    t41 = $[106];
  }
  let t42;
  if ($[107] !== t19 || $[108] !== t39 || $[109] !== t41) {
    t42 = <PanelContent className="flex flex-col gap-4">{t19}{t39}{t41}</PanelContent>;
    $[107] = t19;
    $[108] = t39;
    $[109] = t41;
    $[110] = t42;
  } else {
    t42 = $[110];
  }
  let t43;
  if ($[111] !== panelRef || $[112] !== t15 || $[113] !== t42) {
    t43 = <Panel ref={panelRef} className={t15} data-testid="portfolio-activity">{t42}</Panel>;
    $[111] = panelRef;
    $[112] = t15;
    $[113] = t42;
    $[114] = t43;
  } else {
    t43 = $[114];
  }
  return t43;
}